import React from 'react';
import * as Sentry from '@sentry/react';
import './ErrorBoundary.scss';

/**
 * Error Fallback Component
 * Displays a user-friendly error message when an error occurs
 */
const ErrorFallback = ({ error, resetError }) => {
  const isDevelopment = import.meta.env.DEV;

  const handleReload = () => {
    window.location.reload();
  };

  const handleReset = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="error-boundary" role="alert">
      <div className="error-boundary__content">
        <div className="error-boundary__icon">
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
              fill="currentColor"
            />
          </svg>
        </div>

        <h1 className="error-boundary__title">Something went wrong</h1>
        <p className="error-boundary__message">
          We're sorry for the inconvenience. An unexpected error has occurred.
          Please try refreshing the page or contact support if the problem persists.
        </p>

        <div className="error-boundary__actions">
          <button
            type="button"
            onClick={handleReset}
            className="error-boundary__button error-boundary__button--primary"
            aria-label="Try again"
          >
            Try Again
          </button>
          <button
            type="button"
            onClick={handleReload}
            className="error-boundary__button error-boundary__button--secondary"
            aria-label="Reload page"
          >
            Reload Page
          </button>
        </div>

        {isDevelopment && error && (
          <details className="error-boundary__details">
            <summary className="error-boundary__summary">
              Error Details (Development Only)
            </summary>
            <div className="error-boundary__error-info">
              <pre className="error-boundary__error-message">
                {error.toString()}
              </pre>
              {error.stack && (
                <pre className="error-boundary__error-stack">{error.stack}</pre>
              )}
            </div>
          </details>
        )}
      </div>
    </div>
  );
};

/**
 * Global Error Boundary Component
 * Wraps the app and catches React rendering errors
 */
const ErrorBoundary = ({ children }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={ErrorFallback}
      showDialog={false}
      beforeCapture={(scope, error, errorInfo) => {
        // Add additional context before capturing
        scope.setTag('errorBoundary', true);
        scope.setContext('react', {
          componentStack: errorInfo.componentStack,
        });
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;

